import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { TiingoNewsArticle } from '../api/newsTypes';
import { Badge } from './shared';
import { hoverScrollbar } from '../../../styles/hoverScrollbar';

// --- Helpers ---

function isSafeUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
        return false;
    }
}

function relativeTime(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    if (Number.isNaN(then)) return '';
    const diffMs = now - then;
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

function filterTags(
    article: TiingoNewsArticle,
    allArticles: TiingoNewsArticle[],
    ticker: string,
): string[] {
    // 1. Deduplicate (case-insensitive), keep first casing seen
    const seen = new Map<string, string>();
    for (const tag of (article.tags ?? [])) {
        const lower = tag.toLowerCase();
        if (!seen.has(lower)) seen.set(lower, tag);
    }

    // 2. Remove current asset ticker/name
    const tickerLower = ticker.toLowerCase();
    const deduped = [...seen.entries()].filter(
        ([lower]) => lower !== tickerLower,
    );

    // 3. Rank by specificity: tags appearing in fewer articles are more specific
    const tagCounts = new Map<string, number>();
    for (const a of allArticles) {
        const articleSeen = new Set<string>();
        for (const t of (a.tags ?? [])) {
            const l = t.toLowerCase();
            if (!articleSeen.has(l)) {
                articleSeen.add(l);
                tagCounts.set(l, (tagCounts.get(l) || 0) + 1);
            }
        }
    }

    return deduped
        .sort((a, b) => {
            const countA = tagCounts.get(a[0]) || 0;
            const countB = tagCounts.get(b[0]) || 0;
            if (countA !== countB) return countA - countB;
            return a[1].localeCompare(b[1]);
        })
        .slice(0, 3)
        .map(([, original]) => original);
}

// --- Article Card ---

interface ArticleCardProps {
    article: TiingoNewsArticle;
    allArticles: TiingoNewsArticle[];
    ticker: string;
    onHover: (article: TiingoNewsArticle | null) => void;
    isSelected?: boolean;
    onSelect?: (article: TiingoNewsArticle) => void;
}

function ArticleCard({ article, allArticles, ticker, onHover, isSelected, onSelect }: ArticleCardProps) {
    const theme = useTheme();
    const tags = useMemo(
        () => filterTags(article, allArticles, ticker),
        [article, allArticles, ticker],
    );

    return (
        <Box
            onMouseEnter={() => onHover(article)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onSelect?.(article)}
            sx={{
                py: 1.25,
                px: 1,
                mx: -1,
                borderBottom: 1,
                borderColor: 'divider',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                ...(isSelected && {
                    bgcolor: `${theme.palette.info.main}14`,
                    borderLeft: 2,
                    borderLeftColor: theme.palette.info.main,
                }),
            }}
        >
            <Typography
                sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    lineHeight: 1.35,
                    mb: 0.5,
                }}
            >
                {article.title}
            </Typography>

            <Typography
                sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    lineHeight: 1.35,
                    mb: 0.75,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {article.description}
            </Typography>

            {tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px', mb: 0.75 }}>
                    {tags.map(tag => (
                        <Badge
                            key={tag}
                            color={theme.palette.primary.main}
                            sx={{ fontSize: '9px', py: '1px', px: '5px' }}
                        >
                            {tag}
                        </Badge>
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                        sx={{ fontSize: '10px', color: 'text.secondary' }}
                    >
                        {article.source}
                    </Typography>
                    <Typography
                        sx={{ fontSize: '10px', color: 'text.disabled' }}
                    >
                        {relativeTime(article.publishedDate)}
                    </Typography>
                </Box>
                <Typography
                    component="a"
                    href={isSafeUrl(article.url) ? article.url : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        fontSize: '10px',
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    Read &#8599;
                </Typography>
            </Box>
        </Box>
    );
}

// --- NewsPanel ---

export interface NewsPanelProps {
    articles: TiingoNewsArticle[];
    ticker: string;
    onHoverArticle: (article: TiingoNewsArticle | null) => void;
    selectedArticleIds?: Set<number>;
    onSelectArticle?: (article: TiingoNewsArticle) => void;
}

export function NewsPanel({ articles, ticker, onHoverArticle, selectedArticleIds, onSelectArticle }: NewsPanelProps) {
    if (articles.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                }}
            >
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    No news available
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.25,
                    px: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    News
                </Typography>
                <Typography
                    sx={{ fontSize: '10px', color: 'text.disabled' }}
                >
                    {articles.length} article{articles.length !== 1 ? 's' : ''}
                </Typography>
            </Box>

            <Box sx={{ px: 1.5, ...hoverScrollbar, height: '100%' }}>
                {articles.map(article => (
                    <ArticleCard
                        key={article.id}
                        article={article}
                        allArticles={articles}
                        ticker={ticker}
                        onHover={onHoverArticle}
                        isSelected={selectedArticleIds?.has(article.id)}
                        onSelect={onSelectArticle}
                    />
                ))}
            </Box>
        </Box>
    );
}
